import { FC, useMemo } from 'react';

import { sortEmployerUsers } from 'utils/clickme';
import { ACCOUNTS_USER_TAG_LIST_MAX_USERS } from 'utils/constants/accounts';

import { ChipsContainer, CustomChip, Link, Text } from '@hh.ru/magritte-ui';

import useTrls from 'hooks/translation/useTrls';

import { setAccountsAccountUsers, setAccountsEmployerUsers } from 'store/models/accounts';
import { useDispatch, useSelector } from 'store/utils';

interface UsersTagListProps {
    needEmptyText?: boolean;
    isShort?: boolean;
    openAllUsers?: () => void;
}

export const UsersTagList: FC<UsersTagListProps> = ({ needEmptyText, isShort, openAllUsers }) => {
    const { employerUsers, accountUsers } = useSelector((state) => state.accounts);

    const dispatch = useDispatch();

    const trl = useTrls();

    const maxItems = isShort ? ACCOUNTS_USER_TAG_LIST_MAX_USERS : accountUsers.length;

    const filteredEmployerUsers = useMemo(
        () => [...employerUsers].filter((user) => accountUsers.includes(user.id.toString())).slice(0, maxItems),
        [employerUsers, maxItems, accountUsers]
    );

    if (accountUsers.length === 0 && needEmptyText) {
        return <Text typography="paragraph-2-regular">{trl('account.create.emptyUsers')}</Text>;
    }

    return (
        <>
            <ChipsContainer correction={8}>
                {filteredEmployerUsers.map(({ id, name }) => (
                    <CustomChip
                        key={id}
                        size="small"
                        Element="button"
                        onDelete={() => {
                            const users = accountUsers.filter((user) => user !== id.toString());
                            dispatch([
                                setAccountsAccountUsers(users),
                                setAccountsEmployerUsers(sortEmployerUsers(employerUsers, users)),
                            ]);
                        }}
                        deleteOnly
                    >
                        {name}
                    </CustomChip>
                ))}
            </ChipsContainer>
            {isShort && openAllUsers && accountUsers.length > maxItems && (
                <Link Element="button" onClick={openAllUsers}>
                    {trl('accounts.create.users.showAll')}
                </Link>
            )}
        </>
    );
};
